import type { Navigate } from '../App'
import { useIsland, useStore } from '../state/store'
import { readiness } from '../lib/analysis'
import { dueLabel } from '../lib/srs'
import { SpeakButton } from '../components/SpeakButton'
import { ToneReference } from '../components/ToneRibbon'
import { languageOf } from '../lib/languages'

export function IslandView({ id, navigate }: { id: string; navigate: Navigate }) {
  const island = useIsland(id)
  const { dispatch } = useStore()

  if (!island) {
    return (
      <div className="empty">
        That island is gone.{' '}
        <button type="button" className="btn is-small" onClick={() => navigate({ view: 'islands' })}>
          Back to islands
        </button>
      </div>
    )
  }

  const lang = languageOf(island.language)
  const state = readiness(island)
  const whole = island.blocks.map((b) => b.hanzi).join('')

  return (
    <>
      <button
        type="button"
        className="btn is-quiet is-small"
        onClick={() => navigate({ view: 'islands' })}
        style={{ marginBottom: '0.8rem' }}
      >
        ← Islands
      </button>

      <header className="page-head">
        <div className="eyebrow">
          {island.title} · {lang.code === 'zh' ? 'HSK' : 'Level'} {island.hskLevel}
        </div>
        <h1 className="page-title">
          <span className="page-title-zh">{island.titleZh}</span>
        </h1>
        {island.useWhen && <p className="lede">{island.useWhen}</p>}
      </header>

      <div className="row" style={{ marginBottom: '1.2rem' }}>
        <span className={`badge is-${state}`}>{state}</span>
        <span className={`badge ${island.srs.due && island.srs.due <= Date.now() ? 'is-due' : ''}`}>
          {dueLabel(island.srs)}
        </span>
        {island.stats.lastScore !== null && (
          <span className="badge">last {island.stats.lastScore}</span>
        )}
        <span className="badge">best {island.stats.bestScore}</span>
      </div>

      <div className="row" style={{ marginBottom: '1.6rem' }}>
        <button
          type="button"
          className="btn"
          onClick={() => navigate({ view: 'listen', id: island.id })}
        >
          Listen
        </button>
        <button
          type="button"
          className="btn is-primary"
          onClick={() => navigate({ view: 'recall', id: island.id })}
        >
          Recall
        </button>
        <button
          type="button"
          className="btn is-live"
          onClick={() => navigate({ view: 'perform', id: island.id })}
        >
          Perform against the clock
        </button>
        <SpeakButton text={whole} language={lang.code} label="Play" className="btn" withSlow />
        <button
          type="button"
          className="btn is-quiet"
          onClick={() => navigate({ view: 'edit', id: island.id })}
        >
          Edit
        </button>
      </div>

      {island.isStarter && (
        <div className="notice" style={{ marginBottom: '1.2rem' }}>
          This is starter content, and the details are not yours. Rewrite it before you memorise
          it — a fluent sentence about a life you don't have is worse than useless in a real
          conversation.{' '}
          <button
            type="button"
            className="btn is-small is-quiet"
            onClick={() => navigate({ view: 'edit', id: island.id })}
          >
            Make it mine
          </button>
        </div>
      )}

      <div className="card">
        {island.blocks.map((block, i) => (
          <div key={block.id} className="block-row">
            <div className="block-index">{String(i + 1).padStart(2, '0')}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className={lang.code === 'zh' ? 'hanzi' : ''} style={{ fontSize: '1.3rem' }}>
                {block.hanzi || <span className="blank">empty block</span>}
              </div>
              {lang.hasPhonetics && <div className="pinyin">{block.pinyin}</div>}
              <div className="english" style={{ fontSize: '0.9rem' }}>
                {block.english}
              </div>
              {lang.hasPhonetics && <ToneReference pinyin={block.pinyin} />}
              {block.note && (
                <p className="muted" style={{ fontSize: '0.84rem', margin: '0.3rem 0 0' }}>
                  {block.note}
                </p>
              )}
            </div>
            <div>
              <SpeakButton text={block.hanzi} language={lang.code} withSlow />
            </div>
          </div>
        ))}
      </div>

      <div className="divider" />

      <div className="row row-end">
        <button
          type="button"
          className="btn is-small is-danger"
          onClick={() => {
            const ok = window.confirm(
              `Delete "${island.title}"? Its practice history goes with it, and any scenario beat that points at it will be removed.`,
            )
            if (ok) {
              dispatch({ type: 'deleteIsland', id: island.id })
              navigate({ view: 'islands' })
            }
          }}
        >
          Delete island
        </button>
      </div>
    </>
  )
}
